import { useHouseholdSize } from '@hooks/useHouseholdSize';
import { useFeatureValue } from '@monofront/business/contexts';
import { CONFIG_AVERAGE_PRODUCT_BASKET } from '@monofront/business/constants';
import { prismicService, productService } from '@services/businessContainer';
import { AverageProductBasketDocument } from '@monofront/slice-machine/prismicio-types';
import { isNumber, sift } from 'radash';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { IProduct, IShoppingCartProduct } from '@monofront/types';
import useI18n from '@hooks/i18n/useI18n';
import { getProductPricing } from '@monofront/business/utils';

export const useAverageProductBasket = () => {
  const { locale } = useRouter();
  const { channel } = useI18n();
  const { size: currentHousehold } = useHouseholdSize();
  const averageProductBasketConfig = useFeatureValue(
    ...CONFIG_AVERAGE_PRODUCT_BASKET,
  );
  const [products, setProducts] = useState<IShoppingCartProduct[]>([]);
  const [prices, setPrices] = useState<{
    nonMemberTotal: number;
    total: number;
  }>({
    nonMemberTotal: 0,
    total: 0,
  });

  const householdSizes = useMemo(
    () =>
      sift(
        Object.keys(averageProductBasketConfig).map((key) => {
          const keyToNum = Number(key);

          return isNumber(keyToNum) ? keyToNum : undefined;
        }),
      ),
    [averageProductBasketConfig],
  );

  const selectedHouseholdAverageProductsBasket = useMemo(() => {
    return householdSizes.includes(currentHousehold)
      ? averageProductBasketConfig[currentHousehold]
      : null;
  }, [householdSizes, currentHousehold]);

  useEffect(() => {
    const fetchAndSet = async () => {
      if (!selectedHouseholdAverageProductsBasket) return;

      const document =
        await prismicService.getDocumentByUid<AverageProductBasketDocument>(
          'average_product_basket',
          selectedHouseholdAverageProductsBasket as string,
          undefined,
          locale ? { lang: `${locale}-${locale}` } : {},
        );

      const averageProductBasketDocument = document.data;

      if (!averageProductBasketDocument?.products?.length) return;

      const productsSKUs = averageProductBasketDocument.products.map(
        ({ sku }) => sku,
      );

      const products = await productService.getProductsBySku(
        productsSKUs,
        channel,
        false,
      );

      const { productsWithQuantities, nonMemberTotal, total } = products.reduce(
        (acc, product) => {
          const foundProduct = averageProductBasketDocument.products.find(
            ({ sku }) => sku === product.sku,
          );

          if (!foundProduct) return acc;

          const quantity = Number(foundProduct.product_quantity);
          const { price, compareAtPrice } = getProductPricing(
            {
              price: product.price,
              compare_at_price: product.compare_at_price,
              tags: product.tags,
              meta: product.meta,
            },
            1,
          );

          acc.nonMemberTotal += quantity * compareAtPrice;
          acc.total += quantity * price;

          acc.productsWithQuantities.push({
            ...product,
            quantity,
            membershipPriceSubtotal: price * quantity,
          });

          return acc;
        },
        {
          productsWithQuantities: [] as Array<
            IProduct & {
              quantity: number;
              membershipPriceSubtotal: number;
            }
          >,
          nonMemberTotal: 0,
          total: 0,
        },
      );

      setProducts(sift(productsWithQuantities));
      setPrices({ nonMemberTotal, total });
    };

    void fetchAndSet();
  }, [selectedHouseholdAverageProductsBasket, locale, channel]);

  return {
    householdSizes,
    products,
    prices,
    savings: prices.total - prices.nonMemberTotal,
  };
};
